import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { signIn, fetchAuthSession, getCurrentUser, signOut as amplifySignOut, resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import { toast } from 'react-toastify';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordResetStep, setPasswordResetStep] = useState<'email' | 'code'>('email');
  const { refreshUser } = useAuth();
  const navigate = useNavigate();

  const clearExistingSession = async () => {
    try {
      // Check if there's an existing user session
      await getCurrentUser();
      // If we get here, there's an existing session - sign out
      console.log('🔍 Existing session detected, signing out...');
      try {
        await amplifySignOut();
      } catch (signOutError) {
        console.log('⚠️ Error during signOut, continuing...', signOutError);
      }
      // Clear localStorage
      localStorage.removeItem('cognitoToken');
      // Clear any Amplify-related storage keys
      Object.keys(localStorage).forEach(key => {
        if (key.includes('CognitoIdentityServiceProvider') || key.includes('aws-amplify')) {
          localStorage.removeItem(key);
        }
      });
      // Clear sessionStorage
      sessionStorage.clear();
      console.log('✅ Existing session cleared');
    } catch (error: any) {
      // No existing session, which is fine
      console.log('🔍 No existing session found, proceeding with login');
      // Still clear localStorage to be safe
      localStorage.removeItem('cognitoToken');
      // Clear any Amplify-related storage keys
      Object.keys(localStorage).forEach(key => {
        if (key.includes('CognitoIdentityServiceProvider') || key.includes('aws-amplify')) {
          localStorage.removeItem(key);
        }
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Clear any existing session before attempting new sign-in
      await clearExistingSession();

      const result = await signIn({
        username: email,
        password: password
      });
      
      if (result.isSignedIn) {
        const session = await fetchAuthSession();
        if (session.tokens?.accessToken) {
          localStorage.setItem('cognitoToken', session.tokens.accessToken.toString());
        }
        
        await refreshUser();
        toast.success('Login successful!');
        navigate('/dashboard');
      } else {
        toast.error('Login failed. Please check your credentials.');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      
      // Handle specific error about existing signed-in user
      if (error.message?.includes('already') || error.message?.includes('signed in') || 
          error.name === 'AlreadySignedInException' || error.code === 'AlreadySignedIn') {
        // Try to clear and retry once
        console.log('🔍 Retrying after clearing session...');
        try {
          await clearExistingSession();
          const retryResult = await signIn({
            username: email,
            password: password
          });
          
          if (retryResult.isSignedIn) {
            const session = await fetchAuthSession();
            if (session.tokens?.accessToken) {
              localStorage.setItem('cognitoToken', session.tokens.accessToken.toString());
            }
            await refreshUser();
            toast.success('Login successful!');
            navigate('/dashboard');
            return;
          }
        } catch (retryError: any) {
          console.error('Retry login error:', retryError);
          toast.error('Please try signing out first, then sign in again.');
        }
      } else if (error.name === 'UserNotConfirmedException') {
        toast.error('Please verify your email before signing in');
        navigate('/register', { state: { email, needsVerification: true } });
      } else if (error.name === 'NotAuthorizedException') {
        toast.error('Incorrect email or password');
      } else if (error.name === 'UserNotFoundException') {
        toast.error('No account found with this email');
      } else {
        toast.error(error.message || 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotPasswordEmail) {
      toast.error('Please enter your email address');
      return;
    }

    setForgotPasswordLoading(true);
    try {
      await resetPassword({ username: forgotPasswordEmail });
      toast.success('Password reset code sent to your email!');
      setPasswordResetStep('code');
    } catch (error: any) {
      console.error('Forgot password error:', error);
      if (error.name === 'UserNotFoundException') {
        toast.error('No account found with this email address');
      } else if (error.name === 'LimitExceededException') {
        toast.error('Too many attempts. Please try again later.');
      } else {
        toast.error(error.message || 'Failed to send reset code. Please try again.');
      }
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleConfirmPasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!resetCode || !newPassword || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }

    setForgotPasswordLoading(true);
    try {
      await confirmResetPassword({
        username: forgotPasswordEmail,
        confirmationCode: resetCode,
        newPassword: newPassword
      });
      toast.success('Password reset successfully! You can now sign in with your new password.');
      setShowForgotPassword(false);
      setForgotPasswordEmail('');
      setResetCode('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordResetStep('email');
    } catch (error: any) {
      console.error('Confirm password reset error:', error);
      if (error.name === 'CodeMismatchException') {
        toast.error('Invalid verification code. Please check and try again.');
      } else if (error.name === 'ExpiredCodeException') {
        toast.error('Verification code has expired. Please request a new one.');
        setPasswordResetStep('email');
      } else if (error.name === 'InvalidPasswordException') {
        toast.error('Password does not meet requirements. Please use a stronger password.');
      } else {
        toast.error(error.message || 'Failed to reset password. Please try again.');
      }
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  return (
    <div className="login-page">
      {/* Desktop Header - Hidden on Mobile */}
      <div className="login-header desktop-header">
        <div className="login-header-content">
          <div className="login-logo-section">
            <img 
              src="https://oarayouthinnovationlab.ca/wp-content/uploads/2025/08/cropped-Logo-1-83x75-1.webp" 
              alt="OARA Logo" 
              className="oara-logo"
            />
            <div className="login-title-section">
              <h1>OARA YOUTH INNOVATION LAB</h1>
              <p className="login-subtitle">Learning Management Portal</p>
            </div>
          </div>
          <div className="login-contact-info">
            <p><strong>Downtown Vernon Makerspace</strong></p>
            <p>3125 31 Ave, Vernon, BC V1T 2H1</p>
            <p>Phone: <a href="tel:2367950470">(236) 795-0470</a></p>
            <p>Email: <a href="mailto:oarayouthinnovationlab@gmail.com">oarayouthinnovationlab@gmail.com</a></p>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="card">
          {/* Mobile Branding - Only visible on mobile */}
          <div className="mobile-login-branding">
            <img 
              src="https://oarayouthinnovationlab.ca/wp-content/uploads/2025/08/cropped-Logo-1-83x75-1.webp" 
              alt="OARA Logo" 
              className="mobile-logo"
            />
            <h1 className="mobile-login-title">OARA</h1>
            <p className="mobile-login-subtitle">Learning Management Portal</p>
          </div>

          <h2 className="login-form-title">Sign In</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Enter your email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Enter your password"
            />
            <div style={{ textAlign: 'right', marginTop: '8px' }}>
              <button
                type="button"
                onClick={() => {
                  setShowForgotPassword(true);
                  setForgotPasswordEmail(email);
                  setPasswordResetStep('email');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#667eea',
                  cursor: 'pointer',
                  fontSize: '14px',
                  textDecoration: 'underline',
                  padding: 0
                }}
              >
                Forgot Password?
              </button>
            </div>
          </div>

          <button type="submit" className="btn btn-login" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div className="login-footer">
          <p>
            Don't have an account?{' '}
            <Link to="/register" className="create-account-link">
              Create Account
            </Link>
          </p>
        </div>
      </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="modal-overlay" onClick={() => {
          setShowForgotPassword(false);
          setPasswordResetStep('email');
          setForgotPasswordEmail('');
          setResetCode('');
          setNewPassword('');
          setConfirmPassword('');
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', width: '90%' }}>
            <div className="modal-header">
              <h2>Reset Password</h2>
              <button
                className="close-button"
                onClick={() => {
                  setShowForgotPassword(false);
                  setPasswordResetStep('email');
                  setForgotPasswordEmail('');
                  setResetCode('');
                  setNewPassword('');
                  setConfirmPassword('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {passwordResetStep === 'email' ? (
                <form onSubmit={handleForgotPassword}>
                  <p style={{ marginBottom: '20px', color: '#666' }}>
                    Enter your email address and we'll send you a verification code to reset your password.
                  </p>
                  <div className="form-group">
                    <label htmlFor="forgot-email">Email</label>
                    <input
                      type="email"
                      id="forgot-email"
                      value={forgotPasswordEmail}
                      onChange={(e) => setForgotPasswordEmail(e.target.value)}
                      required
                      placeholder="Enter your email"
                      autoFocus
                    />
                  </div>
                  <div className="form-actions" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowForgotPassword(false);
                        setPasswordResetStep('email');
                        setForgotPasswordEmail('');
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={forgotPasswordLoading}
                    >
                      {forgotPasswordLoading ? 'Sending...' : 'Send Reset Code'}
                    </button>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleConfirmPasswordReset}>
                  <p style={{ marginBottom: '20px', color: '#666' }}>
                    We've sent a verification code to <strong>{forgotPasswordEmail}</strong>. 
                    Enter the code and your new password below.
                  </p>
                  <div className="form-group">
                    <label htmlFor="reset-code">Verification Code</label>
                    <input
                      type="text"
                      id="reset-code"
                      value={resetCode}
                      onChange={(e) => setResetCode(e.target.value.replace(/\D/g, ''))}
                      required
                      placeholder="Enter 6-digit code"
                      maxLength={6}
                      autoFocus
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="new-password">New Password</label>
                    <input
                      type="password"
                      id="new-password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      placeholder="Enter new password (min 8 characters)"
                      minLength={8}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="confirm-password">Confirm New Password</label>
                    <input
                      type="password"
                      id="confirm-password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      placeholder="Confirm new password"
                      minLength={8}
                    />
                  </div>
                  <div className="form-actions" style={{ display: 'flex', gap: '10px', justifyContent: 'space-between', marginTop: '20px' }}>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setPasswordResetStep('email');
                        setResetCode('');
                        setNewPassword('');
                        setConfirmPassword('');
                      }}
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={forgotPasswordLoading}
                    >
                      {forgotPasswordLoading ? 'Resetting...' : 'Reset Password'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Footer with Organization Details */}
      <footer className="login-page-footer">
        <div className="login-footer-content">
          <h3>OARA Youth Innovation Lab</h3>
          <div className="login-footer-details">
            <p><strong>Downtown Vernon Makerspace</strong></p>
            <p>3125 31 Ave, Vernon, BC V1T 2H1</p>
            <p>
              <a href="tel:2367950470">Phone: (236) 795-0470</a>
            </p>
            <p>
              <a href="mailto:oarayouthinnovationlab@gmail.com">Email: oarayouthinnovationlab@gmail.com</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Login;